import React from 'react';
import { Eye, Maximize2, RefreshCw, ShieldCheck } from 'lucide-react';
import { userService } from '../../services/api';
import { dashboardService } from '../../services/dashboardService';
import { AdminDashboardSummary, DepartmentSummary } from '../../types/domain';
import { useReferenceOptions } from '../../hooks/useReferenceData';
import { todayISO } from '../../utils/dateValidation';
import { canonicalStatusKey, canonicalStatusLabel } from '../../utils/statusPresentation';
import DismissibleError from '../DismissibleError';
import {
  CompactTableToolbar,
  EmptyTableState,
  FormModal,
  RecordsTableCard,
  StatusBadge,
  TypeBadge,
} from '../records';

type GovernanceTab =
  | 'users'
  | 'firearms'
  | 'officers'
  | 'departments'
  | 'custody';

interface GovernanceFilters {
  search: string;
  role: string;
  status: string;
  serviceability: string;
  employment: string;
  ltopf: string;
  departmentType: string;
  warning: string;
  workflow: string;
  action: string;
  department: string;
  date: string;
}

interface DetailState {
  title: string;
  subtitle?: string;
  fields: Array<[string, React.ReactNode]>;
}

const emptyFilters: GovernanceFilters = {
  search: '',
  role: '',
  status: '',
  serviceability: '',
  employment: '',
  ltopf: '',
  departmentType: '',
  warning: '',
  workflow: '',
  action: '',
  department: '',
  date: '',
};

const tabLabels: Record<GovernanceTab, string> = {
  users: 'Unassigned Users',
  firearms: 'Unassigned Firearms',
  officers: 'Unassigned Officers',
  departments: 'Department Coverage',
  custody: 'Custody Events',
};

const tabs: GovernanceTab[] = ['users', 'firearms', 'officers', 'departments', 'custody'];
const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'inactive', label: 'Inactive' },
];
const DEPARTMENT_TYPE_OPTIONS = [
  { value: 'station', label: 'Station' },
  { value: 'unit', label: 'Unit' },
];
const CUSTODY_WORKFLOW_OPTIONS = [
  { value: 'issuance', label: 'Issuance' },
  { value: 'return', label: 'Return' },
  { value: 'incident', label: 'Incident' },
  { value: 'firearm_record_update', label: 'Firearm Record Update' },
  { value: 'officer_record_update', label: 'Officer Record Update' },
  { value: 'are_upload', label: 'ARE Upload' },
  { value: 'firearm_image_upload', label: 'Firearm Image Upload' },
];
const CUSTODY_ACTION_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'status_changed', label: 'Status Changed' },
  { value: 'document_uploaded', label: 'Document Uploaded' },
  { value: 'image_uploaded', label: 'Image Uploaded' },
  { value: 'record_updated', label: 'Record Updated' },
  { value: 'delete_attempted', label: 'Delete Attempted' },
];

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const compactValue = (input: unknown): string => value(input, '');

const formatDate = (input?: string | null): string => {
  if (!input) return '-';
  const parsed = new Date(input);
  return Number.isNaN(parsed.getTime()) ? input : parsed.toLocaleDateString();
};

const dateKey = (input?: string | null): string => value(input, '').slice(0, 10);

const titleText = (input?: string): string =>
  value(input)
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const previewText = (input: unknown, limit = 56): string => {
  const text = value(input).replace(/\s+/g, ' ').trim();
  return text.length > limit ? `${text.slice(0, limit - 1)}...` : text;
};

const matchesSearch = (parts: unknown[], search: string): boolean =>
  !search || parts.map((part) => compactValue(part)).join(' ').toLowerCase().includes(search.toLowerCase());

const isDepartmentScopedRole = (roleCode?: string | null) =>
  roleCode === 'ACCOUNTABLE_PNP_OFFICER' || roleCode === 'CENTRAL_LOGISTICS_OFFICER';

const ltopfStatus = (officer: any): 'valid' | 'expiring_soon' | 'expired' | 'missing' => {
  if (!officer.ltopf_expiration_date) return 'missing';
  if (officer.ltopf_expiration_date < todayISO()) return 'expired';
  const expiration = new Date(`${officer.ltopf_expiration_date}T00:00:00`);
  const now = new Date(`${todayISO()}T00:00:00`);
  const daysRemaining = Math.round((expiration.getTime() - now.getTime()) / 86_400_000);
  return daysRemaining <= 60 ? 'expiring_soon' : 'valid';
};

const canonicalFirearmStatus = (status?: string): string => ({
  in_storage: 'available',
  issued: 'issued',
  returned: 'returned',
  unserviceable: 'for_repair',
  beyond_economical_repair: 'inactive',
  lost: 'inactive',
  stolen: 'inactive',
  archived: 'archived',
}[status || ''] || status || '');

const departmentWarnings = (department: any): string[] => [
  !department.logistics_users && 'No Logistics Officer assigned',
  !department.accountable_users && 'No Accountable Officer assigned',
  !department.firearms && 'No firearms registered',
  !department.officers && 'No officers registered',
].filter(Boolean) as string[];

const MetricCard: React.FC<{ label: string; value: unknown; note: string }> = ({ label, value: metric, note }) => (
  <article className="stat-card">
    <span className="metric-icon blue"><ShieldCheck size={20} /></span>
    <div>
      <span className="stat-label">{label}</span>
      <strong className="stat-number">{value(metric, '0')}</strong>
      <span className="stat-note">{note}</span>
    </div>
  </article>
);

const SystemGovernance: React.FC = () => {
  const [summary, setSummary] = React.useState<AdminDashboardSummary | null>(null);
  const [departments, setDepartments] = React.useState<DepartmentSummary[]>([]);
  const [selections, setSelections] = React.useState<Record<number, string>>({});
  const [firearmSelections, setFirearmSelections] = React.useState<Record<number, string>>({});
  const [firearmReasons, setFirearmReasons] = React.useState<Record<number, string>>({});
  const [officerSelections, setOfficerSelections] = React.useState<Record<number, string>>({});
  const [officerReasons, setOfficerReasons] = React.useState<Record<number, string>>({});
  const [filters, setFilters] = React.useState<Record<GovernanceTab, GovernanceFilters>>({
    users: { ...emptyFilters },
    firearms: { ...emptyFilters },
    officers: { ...emptyFilters },
    departments: { ...emptyFilters },
    custody: { ...emptyFilters },
  });
  const [activeTab, setActiveTab] = React.useState<GovernanceTab>('users');
  const [expanded, setExpanded] = React.useState(false);
  const [detail, setDetail] = React.useState<DetailState | null>(null);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const { options: roleOptions } = useReferenceOptions('roles');
  const { options: firearmStatusOptions } = useReferenceOptions('firearm_statuses');
  const { options: firearmConditionOptions } = useReferenceOptions('firearm_conditions');
  const { options: ltopfStatusOptions } = useReferenceOptions('ltopf_statuses');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextSummary, nextDepartments] = await Promise.all([
        dashboardService.getAdminSummary(),
        userService.getDepartments(),
      ]);
      setSummary(nextSummary);
      setDepartments(nextDepartments);
    } catch {
      setError('System governance data is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const assignDepartment = async (user: any) => {
    const departmentId = Number(selections[user.id] || 0);
    if (!departmentId) {
      setError('Select a department before assigning.');
      return;
    }
    try {
      await userService.assignDepartment(user.id, departmentId);
      setMessage(`Department assigned to ${user.username}.`);
      setSelections({ ...selections, [user.id]: '' });
      await load();
    } catch {
      setError('Department assignment could not be saved.');
    }
  };

  const assignFirearmDepartment = async (firearm: any) => {
    const departmentId = Number(firearmSelections[firearm.id] || 0);
    const reason = (firearmReasons[firearm.id] || '').trim();
    if (!departmentId || !reason) {
      setError('Select a department and enter a reason before assigning the firearm.');
      return;
    }
    try {
      await userService.assignFirearmDepartment(firearm.id, { department: departmentId, reason });
      setMessage(`Department assigned to firearm ${value(firearm.serial_number)}.`);
      setFirearmSelections({ ...firearmSelections, [firearm.id]: '' });
      setFirearmReasons({ ...firearmReasons, [firearm.id]: '' });
      await load();
    } catch {
      setError('Firearm department assignment could not be saved.');
    }
  };

  const assignOfficerDepartment = async (officer: any) => {
    const departmentId = Number(officerSelections[officer.id] || 0);
    const reason = (officerReasons[officer.id] || '').trim();
    if (!departmentId || !reason) {
      setError('Select a department and enter a reason before assigning the officer.');
      return;
    }
    try {
      await userService.assignOfficerDepartment(officer.id, { department: departmentId, reason });
      setMessage(`Department assigned to officer ${value(officer.name || officer.badge_number)}.`);
      setOfficerSelections({ ...officerSelections, [officer.id]: '' });
      setOfficerReasons({ ...officerReasons, [officer.id]: '' });
      await load();
    } catch {
      setError('Officer department assignment could not be saved.');
    }
  };

  if (loading) return <div className="loading-state"><span className="loading-mark" />Loading system governance...</div>;
  if (!summary) return <DismissibleError message={error || 'System governance summary returned no data.'} onDismiss={() => setError('')} onRetry={load} />;

  const unassigned = summary.unassigned_summary || {};
  const departmentsSummary = summary.department_summary || {};
  const operational = summary.operational_overview || {};

  const rowsForTab = (tab: GovernanceTab): any[] => {
    switch (tab) {
      case 'users': return summary.unassigned_users || [];
      case 'firearms': return summary.unassigned_firearms || [];
      case 'officers': return summary.unassigned_officers || [];
      case 'departments': return summary.department_coverage || [];
      case 'custody': return summary.recent_custody_events || [];
      default: return [];
    }
  };

  const currentFilter = filters[activeTab];
  const updateFilter = (field: keyof GovernanceFilters, nextValue: string) => {
    setFilters((current) => ({
      ...current,
      [activeTab]: { ...current[activeTab], [field]: nextValue },
    }));
  };
  const clearFilters = () => setFilters((current) => ({ ...current, [activeTab]: { ...emptyFilters } }));

  const filteredRows = (tab: GovernanceTab): any[] => {
    const filter = filters[tab];
    return rowsForTab(tab).filter((row) => {
      switch (tab) {
        case 'users': {
          const role = value(row.role_display || row.role_code, '');
          return matchesSearch([row.username, row.full_name, row.email, role], filter.search)
            && (!filter.role || row.role_code === filter.role)
            && (!filter.date || dateKey(row.date_joined) === filter.date);
        }
        case 'firearms':
          return matchesSearch([row.serial_number, row.article, row.description, row.model, row.status, row.servicability, row.assigned_to_name], filter.search)
            && (!filter.status || canonicalFirearmStatus(row.status) === filter.status)
            && (!filter.serviceability || row.servicability === filter.serviceability)
            && (!filter.date || dateKey(row.created_at) === filter.date);
        case 'officers': {
          const ltopf = ltopfStatus(row);
          return matchesSearch([row.name, row.badge_number, row.rank, row.unit_assignment, row.employment_status, ltopf], filter.search)
            && (!filter.employment || row.employment_status === filter.employment)
            && (!filter.ltopf || ltopf === filter.ltopf)
            && (!filter.date || dateKey(row.created_at) === filter.date);
        }
        case 'departments': {
          const warnings = departmentWarnings(row);
          const status = row.is_active ? 'active' : 'inactive';
          return matchesSearch([row.name, row.code, row.type, status, warnings.join(' ')], filter.search)
            && (!filter.departmentType || row.type === filter.departmentType)
            && (!filter.status || status === filter.status)
            && (!filter.warning || (filter.warning === 'has_warnings' ? warnings.length > 0 : warnings.length === 0));
        }
        case 'custody':
          return matchesSearch([row.workflow_type, row.action_type, row.department, row.firearm_serial, row.officer_name, row.performed_by, row.reviewed_by, row.event_hash, row.remarks], filter.search)
            && (!filter.workflow || row.workflow_type === filter.workflow)
            && (!filter.action || row.action_type === filter.action)
            && (!filter.department || row.department === filter.department)
            && (!filter.date || dateKey(row.created_at) === filter.date);
        default:
          return true;
      }
    });
  };

  const allRows = rowsForTab(activeTab);
  const activeRows = filteredRows(activeTab);

  const openDetail = (nextDetail: DetailState) => setDetail(nextDetail);

  const openCustodyDetail = (event: any) => openDetail({
    title: 'Custody Event Details',
    subtitle: `${value(event.firearm_serial)} | ${formatDate(event.created_at)}`,
    fields: [
      ['Workflow', titleText(event.workflow_type)],
      ['Action', titleText(event.action_type)],
      ['Department', value(event.department)],
      ['Firearm', value(event.firearm_serial)],
      ['Officer', value(event.officer_name)],
      ['Performed By', value(event.performed_by)],
      ['Reviewed By', value(event.reviewed_by)],
      ['Event Hash', <code>{value(event.event_hash)}</code>],
      ['Remarks', <p>{value(event.remarks)}</p>],
    ],
  });

  const renderFilters = () => {
    switch (activeTab) {
      case 'users':
        return (
          <>
            <select aria-label="Role filter" value={currentFilter.role} onChange={(event) => updateFilter('role', event.target.value)}>
              <option value="">All roles</option>
              {roleOptions.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
            </select>
            <input aria-label="Created date filter" type="date" max={todayISO()} value={currentFilter.date} onChange={(event) => updateFilter('date', event.target.value)} />
          </>
        );
      case 'firearms':
        return (
          <>
            <select aria-label="Status filter" value={currentFilter.status} onChange={(event) => updateFilter('status', event.target.value)}>
              <option value="">All statuses</option>
              {firearmStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
            <select aria-label="Serviceability filter" value={currentFilter.serviceability} onChange={(event) => updateFilter('serviceability', event.target.value)}>
              <option value="">All serviceability</option>
              {firearmConditionOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
            <input aria-label="Created date filter" type="date" max={todayISO()} value={currentFilter.date} onChange={(event) => updateFilter('date', event.target.value)} />
          </>
        );
      case 'officers':
        return (
          <>
            <select aria-label="Employment filter" value={currentFilter.employment} onChange={(event) => updateFilter('employment', event.target.value)}>
              <option value="">All employment</option>
              {EMPLOYMENT_STATUS_OPTIONS.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
            <select aria-label="LTOPF status filter" value={currentFilter.ltopf} onChange={(event) => updateFilter('ltopf', event.target.value)}>
              <option value="">All LTOPF</option>
              {ltopfStatusOptions.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
            <input aria-label="Created date filter" type="date" max={todayISO()} value={currentFilter.date} onChange={(event) => updateFilter('date', event.target.value)} />
          </>
        );
      case 'departments':
        return (
          <>
            <select aria-label="Department type filter" value={currentFilter.departmentType} onChange={(event) => updateFilter('departmentType', event.target.value)}>
              <option value="">All types</option>
              {DEPARTMENT_TYPE_OPTIONS.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
            <select aria-label="Status filter" value={currentFilter.status} onChange={(event) => updateFilter('status', event.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select aria-label="Warning filter" value={currentFilter.warning} onChange={(event) => updateFilter('warning', event.target.value)}>
              <option value="">All coverage</option>
              <option value="has_warnings">Has warnings</option>
              <option value="clear">Coverage present</option>
            </select>
          </>
        );
      case 'custody':
        return (
          <>
            <select aria-label="Workflow filter" value={currentFilter.workflow} onChange={(event) => updateFilter('workflow', event.target.value)}>
              <option value="">All workflows</option>
              {CUSTODY_WORKFLOW_OPTIONS.map((workflow) => <option key={workflow.value} value={workflow.value}>{workflow.label}</option>)}
            </select>
            <select aria-label="Action filter" value={currentFilter.action} onChange={(event) => updateFilter('action', event.target.value)}>
              <option value="">All actions</option>
              {CUSTODY_ACTION_OPTIONS.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
            </select>
            <select aria-label="Department filter" value={currentFilter.department} onChange={(event) => updateFilter('department', event.target.value)}>
              <option value="">All departments</option>
              {departments.filter((department) => department.is_active !== false).map((department) => <option key={department.id} value={department.name}>{department.name}</option>)}
            </select>
            <input aria-label="Date filter" type="date" max={todayISO()} value={currentFilter.date} onChange={(event) => updateFilter('date', event.target.value)} />
          </>
        );
      default:
        return null;
    }
  };

  const renderToolbar = (allowExpand: boolean) => (
    <CompactTableToolbar
      className="governance-toolbar"
      searchValue={currentFilter.search}
      searchPlaceholder="Search governance records..."
      onSearchChange={(nextSearch) => updateFilter('search', nextSearch)}
      filters={(
        <>
          {renderFilters()}
          <button className="btn btn-secondary governance-clear-button" type="button" onClick={clearFilters}>Clear</button>
        </>
      )}
      resultCount={`${activeRows.length} of ${allRows.length}`}
      actions={allowExpand ? (
        <button className="icon-button governance-expand-button" type="button" aria-label="Expand governance table" title="Expand table" onClick={() => setExpanded(true)}>
          <Maximize2 size={16} aria-hidden="true" />
        </button>
      ) : null}
    />
  );

  const renderTable = (rows: any[]) => {
    switch (activeTab) {
      case 'users':
        return (
          <table className="records-table governance-table"><thead><tr><th>Username</th><th>Full Name</th><th>Email</th><th>Role</th><th>Created</th><th>Action</th></tr></thead><tbody>
            {rows.length === 0 ? <tr><td colSpan={6}><EmptyTableState title="No unassigned users" description="No users matched the current governance filters." /></td></tr> : rows.map((user) => (
              <tr key={user.id}>
                <td>{value(user.username)}</td>
                <td>{value(user.full_name)}</td>
                <td>{value(user.email)}</td>
                <td><TypeBadge type={user.role_code || 'role'} label={value(user.role_display || titleText(user.role_code))} tone="purple" /></td>
                <td>{formatDate(user.date_joined)}</td>
                <td>{isDepartmentScopedRole(user.role_code) ? (
                  <div className="table-actions">
                    <select value={selections[user.id] || ''} onChange={(event) => setSelections({ ...selections, [user.id]: event.target.value })} aria-label={`Department for ${user.username}`}>
                      <option value="">Select department</option>
                      {departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                    </select>
                    <button className="btn btn-secondary" type="button" onClick={() => assignDepartment(user)}>Assign</button>
                  </div>
                ) : <span>Global role</span>}</td>
              </tr>
            ))}
          </tbody></table>
        );
      case 'firearms':
        return (
          <table className="records-table governance-table"><thead><tr><th>Serial</th><th>Article</th><th>Description / Model</th><th>Status</th><th>Serviceability</th><th>Assigned Officer</th><th>Created</th><th>Action</th></tr></thead><tbody>
            {rows.length === 0 ? <tr><td colSpan={8}><EmptyTableState title="No unassigned firearms" description="No firearm records matched the current governance filters." /></td></tr> : rows.map((firearm) => (
              <tr key={firearm.id}>
                <td>{value(firearm.serial_number)}</td>
                <td>{value(firearm.article)}</td>
                <td>{value(firearm.description)} / {value(firearm.model)}</td>
                <td><StatusBadge status={canonicalStatusKey(firearm) || 'unknown'} label={canonicalStatusLabel(firearm)} /></td>
                <td>{firearm.current_firearm_condition || firearm.servicability_display || titleText(firearm.servicability)}</td>
                <td>{value(firearm.assigned_to_name)}</td>
                <td>{formatDate(firearm.created_at)}</td>
                <td>
                  <div className="table-actions">
                    <select value={firearmSelections[firearm.id] || ''} onChange={(event) => setFirearmSelections({ ...firearmSelections, [firearm.id]: event.target.value })} aria-label={`Department for firearm ${firearm.serial_number}`}>
                      <option value="">Select department</option>
                      {departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                    </select>
                    <input value={firearmReasons[firearm.id] || ''} onChange={(event) => setFirearmReasons({ ...firearmReasons, [firearm.id]: event.target.value })} placeholder="Reason" aria-label={`Assignment reason for firearm ${firearm.serial_number}`} />
                    <button className="btn btn-secondary" type="button" onClick={() => assignFirearmDepartment(firearm)}>Assign</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody></table>
        );
      case 'officers':
        return (
          <table className="records-table governance-table"><thead><tr><th>Officer</th><th>Badge</th><th>Rank</th><th>Department Assignment</th><th>Employment</th><th>LTOPF Expiration</th><th>Created</th><th>Action</th></tr></thead><tbody>
            {rows.length === 0 ? <tr><td colSpan={8}><EmptyTableState title="No unassigned officers" description="No officer records matched the current governance filters." /></td></tr> : rows.map((officer) => (
              <tr key={officer.id}>
                <td>{value(officer.name)}</td>
                <td>{value(officer.badge_number)}</td>
                <td>{value(officer.rank)}</td>
                <td>{value(officer.unit_assignment)}</td>
                <td><StatusBadge status={officer.employment_status || 'unknown'} label={titleText(officer.employment_status)} /></td>
                <td>{formatDate(officer.ltopf_expiration_date)}</td>
                <td>{formatDate(officer.created_at)}</td>
                <td>
                  <div className="table-actions">
                    <select value={officerSelections[officer.id] || ''} onChange={(event) => setOfficerSelections({ ...officerSelections, [officer.id]: event.target.value })} aria-label={`Department for officer ${officer.name}`}>
                      <option value="">Select department</option>
                      {departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                    </select>
                    <input value={officerReasons[officer.id] || ''} onChange={(event) => setOfficerReasons({ ...officerReasons, [officer.id]: event.target.value })} placeholder="Reason" aria-label={`Assignment reason for officer ${officer.name}`} />
                    <button className="btn btn-secondary" type="button" onClick={() => assignOfficerDepartment(officer)}>Assign</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody></table>
        );
      case 'departments':
        return (
          <table className="records-table governance-table"><thead><tr><th>Department</th><th>Type</th><th>Status</th><th>Accountable</th><th>Logistics</th><th>Firearms</th><th>Officers</th><th>Notifications</th><th>Warnings</th></tr></thead><tbody>
            {rows.length === 0 ? <tr><td colSpan={9}><EmptyTableState title="No departments" description="No department records matched the current governance filters." /></td></tr> : rows.map((department) => {
              const warnings = departmentWarnings(department);
              return <tr key={department.id}><td>{department.name} <small>{department.code}</small></td><td><TypeBadge type={department.type || 'department'} label={titleText(department.type)} tone="blue" /></td><td><StatusBadge status={department.is_active ? 'active' : 'inactive'} /></td><td>{department.accountable_users}</td><td>{department.logistics_users}</td><td>{department.firearms}</td><td>{department.officers}</td><td>{department.open_notifications}</td><td><span className="records-truncate" title={warnings.join('; ') || 'Coverage present'}>{warnings.join('; ') || 'Coverage present'}</span></td></tr>;
            })}
          </tbody></table>
        );
      case 'custody':
        return (
          <table className="records-table governance-table"><thead><tr><th>Timestamp</th><th>Workflow</th><th>Action</th><th>Department</th><th>Firearm</th><th>Officer</th><th>Event Hash</th><th>Action</th></tr></thead><tbody>
            {rows.length === 0 ? <tr><td colSpan={8}><EmptyTableState title="No custody events" description="No custody events matched the current governance filters." /></td></tr> : rows.map((event) => (
              <tr key={event.id}><td>{formatDate(event.created_at)}</td><td><TypeBadge type={event.workflow_type || 'workflow'} label={titleText(event.workflow_type)} /></td><td>{titleText(event.action_type)}</td><td>{value(event.department)}</td><td>{value(event.firearm_serial)}</td><td>{value(event.officer_name)}</td><td><span className="records-truncate" title={value(event.event_hash)}>{previewText(event.event_hash, 26)}</span></td><td><button className="btn btn-secondary" type="button" onClick={() => openCustodyDetail(event)}><Eye size={14} />View</button></td></tr>
            ))}
          </tbody></table>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-dashboard inspector-workspace">
      <div className="page-heading">
        <div>
          <h1>System Governance</h1>
          <p>Global oversight for department coverage, orphan records, and custody events.</p>
        </div>
        <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} />Refresh</button>
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} />}

      <div className="stats-grid inspector-stats">
        <MetricCard label="Departments" value={departmentsSummary.total_departments} note={`${value(departmentsSummary.active_departments, '0')} active`} />
        <MetricCard label="Unassigned users" value={unassigned.users_without_department} note="Department-scoped users" />
        <MetricCard label="Unassigned firearms" value={unassigned.firearms_without_department} note="Read-only oversight" />
        <MetricCard label="Unassigned officers" value={unassigned.officers_without_department} note="Read-only oversight" />
        <MetricCard label="Pending issuances" value={operational.pending_issuances} note="Logistics workflow" />
        <MetricCard label="Pending returns" value={operational.pending_returns} note="Logistics workflow" />
      </div>

      <RecordsTableCard
        title="Governance Records"
        description="Review one governance dataset at a time with compact filters and expanded table support."
        meta={`${activeRows.length} of ${allRows.length}`}
        toolbar={renderToolbar(true)}
      >
        <div className="records-segmented-tabs governance-tabs" role="tablist" aria-label="Governance datasets">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={activeTab === tab ? 'active' : ''}
              onClick={() => setActiveTab(tab)}
            >
              {tabLabels[tab]}
            </button>
          ))}
        </div>
        {renderTable(activeRows)}
      </RecordsTableCard>

      {expanded && (
        <FormModal
          open={expanded}
          title={`${tabLabels[activeTab]} Table`}
          description="Expanded governance table view with the same search and filters."
          onClose={() => setExpanded(false)}
          closeLabel="Close expanded table"
          size="lg"
          className="governance-table-modal"
          showExpandToggle={false}
        >
          {renderToolbar(false)}
          <div className="governance-expanded-table">
            {renderTable(activeRows)}
          </div>
        </FormModal>
      )}

      {detail && (
        <FormModal
          open={Boolean(detail)}
          title={detail.title}
          description={detail.subtitle}
          onClose={() => setDetail(null)}
          closeLabel="Close governance details"
          size="lg"
        >
          <div className="detail-grid governance-detail-grid">
            {detail.fields.map(([label, fieldValue]) => (
              <div key={label} className={label === 'Details' || label === 'Remarks' || label === 'Event Hash' ? 'full-width' : undefined}>
                <span>{label}</span>
                <strong>{fieldValue}</strong>
              </div>
            ))}
          </div>
        </FormModal>
      )}
    </div>
  );
};

export default SystemGovernance;
